/**
 * Messages Page
 *
 * Email-based messaging system with conversation threads
 */

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetProfileQuery } from "../../store/api/authApi";
import {
	CreateConversationRequest,
	useCreateConversationMutation,
	useGetConversationByIdQuery,
	useGetConversationsQuery,
	useSendMessageMutation,
} from "../../store/api/conversationsApi";
import { ConversationList } from "./components/ConversationList";
import { ConversationThread } from "./components/ConversationThread";
import { MessageComposer } from "./components/MessageComposer";
import { NewConversationModal } from "./components/NewConversationModal";

export const MessagesPage: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedConversationId, setSelectedConversationId] = useState<
		string | null
	>(null);
	const [isNewConversationModalOpen, setIsNewConversationModalOpen] =
		useState(false);
	const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

	// Fetch current user profile
	const { data: profile } = useGetProfileQuery();
	const currentUserId = profile?.id || "";
	const organizationId = profile?.organization || "";

	// Fetch conversations
	const {
		data: conversations = [],
		isLoading: isLoadingConversations,
		refetch: refetchConversations,
	} = useGetConversationsQuery();

	// Fetch selected conversation details
	const {
		data: selectedConversation,
		isLoading: isLoadingConversation,
		error: conversationError,
		refetch: refetchConversation,
	} = useGetConversationByIdQuery(selectedConversationId || "", {
		skip: !selectedConversationId,
	});

	// Mutations
	const [createConversation, { isLoading: isCreatingConversation }] =
		useCreateConversationMutation();
	const [sendMessage, { isLoading: isSendingMessage }] =
		useSendMessageMutation();

	// Read conversation ID from URL query params
	useEffect(() => {
		const conversationIdFromUrl = searchParams.get("conversation");
		if (conversationIdFromUrl) {
			// Validate that the conversation exists in the list
			const conversationExists = conversations.some(
				(conv) => conv.id === conversationIdFromUrl,
			);
			if (conversationExists) {
				setSelectedConversationId(conversationIdFromUrl);
			} else {
				// Remove invalid conversation ID from URL
				searchParams.delete("conversation");
				setSearchParams(searchParams);
			}
		}
	}, [searchParams, conversations, setSearchParams]);

	// Auto-select first conversation if none selected and no URL param
	useEffect(() => {
		if (
			!selectedConversationId &&
			conversations.length > 0 &&
			!searchParams.get("conversation")
		) {
			setSelectedConversationId(conversations[0].id);
		}
	}, [conversations, selectedConversationId, searchParams]);

	// Handle 404 errors - clear invalid conversation selection
	useEffect(() => {
		if (
			conversationError &&
			"status" in conversationError &&
			conversationError.status === 404
		) {
			setSelectedConversationId(null);
			searchParams.delete("conversation");
			setSearchParams(searchParams);
		}
	}, [conversationError, searchParams, setSearchParams]);

	// Handle creating new conversation
	const handleCreateConversation = async (data: CreateConversationRequest) => {
		try {
			const result = await createConversation(data).unwrap();
			setIsNewConversationModalOpen(false);
			setSelectedConversationId(result.id);
			refetchConversations();
		} catch (error) {
			console.error("Failed to create conversation:", error);
			alert("Failed to create conversation. Please try again.");
		}
	};

	// Handle sending message
	const handleSendMessage = async (content: string) => {
		if (!selectedConversationId) return;

		try {
			await sendMessage({
				conversationId: selectedConversationId,
				data: { content },
			}).unwrap();
			refetchConversation();
		} catch (error) {
			console.error("Failed to send message:", error);
			alert("Failed to send message. Please try again.");
		}
	};

	// Handle selecting conversation
	const handleSelectConversation = (id: string) => {
		setSelectedConversationId(id);
		// Update URL to reflect selected conversation
		searchParams.set("conversation", id);
		setSearchParams(searchParams);
	};

	return (
		<div className="w-full h-full overflow-hidden">
			<div className="flex h-[calc(100vh-8rem)] md:h-[calc(100vh-6rem)] bg-gray-50 relative overflow-hidden">
				{/* Mobile Sidebar Overlay */}
				{isMobileSidebarOpen && (
					<div
						className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
						onClick={() => setIsMobileSidebarOpen(false)}
					/>
				)}

				{/* Left Sidebar - Conversation List */}
				<div
					className={`w-full sm:w-80 flex-shrink-0 bg-white border-r border-gray-200 fixed lg:relative inset-y-0 left-0 z-50 lg:z-auto transform transition-transform duration-300 ease-in-out h-full ${
						isMobileSidebarOpen
							? "translate-x-0"
							: "-translate-x-full lg:translate-x-0"
					}`}
				>
					<ConversationList
						conversations={conversations}
						selectedConversationId={selectedConversationId}
						onSelectConversation={(id) => {
							handleSelectConversation(id);
							setIsMobileSidebarOpen(false);
						}}
						onNewConversation={() => setIsNewConversationModalOpen(true)}
						isLoading={isLoadingConversations}
					/>
				</div>

				{/* Main Content - Conversation Thread */}
				<div className="flex-1 flex flex-col min-w-0 overflow-hidden">
					{selectedConversationId && selectedConversation ? (
						<>
							{/* Conversation Header */}
							<div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
								<div className="flex items-center justify-between gap-2">
									<div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
										{/* Mobile Menu Button */}
										<button
											onClick={() => setIsMobileSidebarOpen(true)}
											className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
											title="Open conversations"
										>
											<svg
												className="w-6 h-6"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M4 6h16M4 12h16M4 18h16"
												/>
											</svg>
										</button>
										<div className="flex-1 min-w-0">
											<h1 className="text-lg md:text-xl font-semibold text-gray-900 truncate">
												{selectedConversation.subject}
											</h1>
											<p className="text-sm text-gray-500 mt-1 truncate">
												{selectedConversation.participant_names?.join(", ") ||
													"No participants"}
											</p>
										</div>
									</div>

									{/* Refresh Button */}
									<button
										onClick={() => refetchConversation()}
										className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
										title="Refresh messages"
									>
										<svg
											className="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
											/>
										</svg>
									</button>
								</div>
							</div>

							{/* Messages Thread */}
							<div className="flex-1 min-h-0 overflow-hidden">
								<ConversationThread
									conversation={selectedConversation}
									currentUserId={currentUserId}
									isLoading={isLoadingConversation}
								/>
							</div>

							{/* Message Composer */}
							<div className="flex-shrink-0">
								<MessageComposer
									onSendMessage={handleSendMessage}
									isSending={isSendingMessage}
									disabled={!selectedConversationId}
								/>
							</div>
						</>
					) : (
						/* Empty State */
						<div className="flex-1 flex items-center justify-center bg-gray-50">
							<div className="text-center max-w-md px-6">
								<svg
									className="w-24 h-24 text-gray-300 mx-auto mb-6"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
									/>
								</svg>
								<h2 className="text-2xl font-semibold text-gray-700 mb-3">
									Welcome to Messages
								</h2>
								<p className="text-gray-500 mb-6">
									{conversations.length === 0
										? "Start a new conversation to communicate with your team members."
										: "Select a conversation from the sidebar to view messages."}
								</p>
								<button
									onClick={() => setIsNewConversationModalOpen(true)}
									className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
								>
									<svg
										className="w-5 h-5"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 4v16m8-8H4"
										/>
									</svg>
									Start New Conversation
								</button>

								{/* Mobile: Show conversation list button */}
								<button
									onClick={() => setIsMobileSidebarOpen(true)}
									className="mt-4 lg:hidden px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center gap-2"
								>
									View Conversations
								</button>
							</div>
						</div>
					)}
				</div>

				{/* New Conversation Modal */}
				<NewConversationModal
					isOpen={isNewConversationModalOpen}
					onClose={() => setIsNewConversationModalOpen(false)}
					onCreateConversation={handleCreateConversation}
					isCreating={isCreatingConversation}
					organizationId={organizationId}
				/>
			</div>
		</div>
	);
};

export default MessagesPage;
